import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, User, MessageSquare, Copy, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import inviteService from '../services/inviteService';
import api from '../services/api';
import { useBusinessContext } from '../contexts/BusinessContext';

const InviteEmployee = () => {
  const navigate = useNavigate();
  const { isOwner } = useBusinessContext();
  const [loading, setLoading] = useState(false);
  const [inviteCreated, setInviteCreated] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // ✅ NEW: Email validation states
  const [emailError, setEmailError] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    message: '',
  });

  // Redirect if not owner
  useEffect(() => {
    if (!isOwner) {
      navigate('/dashboard');
    }
  }, [isOwner, navigate]);

  // ✅ NEW: Secure email validation function
  // Only checks owner's own business via authenticated endpoint
  const validateEmailInTeam = async (email) => {
    if (!email || !email.includes('@')) {
      setEmailError('');
      return true;
    }
    
    try {
      setCheckingEmail(true);
      const response = await api.post('/invite/check-email', { email });
      
      if (response.data.existsInTeam) {
        setEmailError('This email is already a member of your team');
        return false;
      }
      
      if (response.data.hasPendingInvite) {
        setEmailError('There is already a pending invitation for this email');
        return false;
      }
      
      setEmailError('');
      return true;
    } catch (error) {
      console.error('Error checking email:', error);
      // Don't block submission if check fails
      setEmailError('');
      return true;
    } finally {
      setCheckingEmail(false);
    }
  };

  // ✅ NEW: Debounced email validation
  // Waits 500ms after user stops typing before checking
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.email) {
        validateEmailInTeam(formData.email);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ✅ NEW: Validate email one more time before submission
    const isEmailValid = await validateEmailInTeam(formData.email);
    if (!isEmailValid) {
      return;
    }
    
    try {
      setLoading(true);
      const invite = await inviteService.createInvite(formData);
      setInviteCreated(invite);
    } catch (error) {
      console.error('Error creating invite:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create invitation';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await inviteService.copyInviteLink(inviteCreated.inviteLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Error copying link:', error);
      alert('Failed to copy link');
    }
  };

  const handleCreateAnother = () => {
    setInviteCreated(null);
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      message: '',
    });
    setEmailError(''); // ✅ Clear email error
  };

  // Success screen after invite created
  if (inviteCreated) {
    return (
      <div className="p-4 lg:p-8 pb-20 lg:pb-8">
        <div className="max-w-2xl mx-auto">
          {/* Success Card */}
          <div className="card text-center">
            {/* Success Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-600" size={40} />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Invitation Sent!
            </h2>
            <p className="text-gray-600 mb-6">
              Share this link with <strong>{inviteCreated.firstName} {inviteCreated.lastName}</strong> to join your team
            </p>

            {/* Invite Link */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-2">Shareable Invite Link:</p>
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-3">
                <input
                  type="text"
                  readOnly
                  value={inviteCreated.inviteLink}
                  className="flex-1 text-sm text-gray-700 bg-transparent border-none focus:outline-none"
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={handleCopyLink}
                  className="btn btn-secondary text-sm py-1.5 px-3 flex items-center gap-1 flex-shrink-0"
                >
                  {copySuccess ? (
                    <>
                      <CheckCircle size={16} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Expiration Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <div className="text-left flex-1">
                  <p className="text-sm font-medium text-blue-900 mb-1">
                    Invitation expires in 7 days
                  </p>
                  <p className="text-sm text-blue-700">
                    Expires on {new Date(inviteCreated.expiresAt).toLocaleDateString()} at{' '}
                    {new Date(inviteCreated.expiresAt).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Invite Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Invitation Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium text-gray-900">{inviteCreated.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium text-gray-900">
                    {inviteCreated.firstName} {inviteCreated.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="badge bg-yellow-100 text-yellow-700">Pending</span>
                </div>
                {inviteCreated.message && (
                  <div className="pt-2 border-t border-gray-200">
                    <span className="text-gray-600">Message:</span>
                    <p className="text-gray-900 mt-1 italic">"{inviteCreated.message}"</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCreateAnother}
                className="btn btn-primary flex-1"
              >
                Invite Another Employee
              </button>
              <button
                onClick={() => navigate('/team')}
                className="btn btn-secondary flex-1"
              >
                View All Invitations
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invite form
  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/team')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft size={20} />
          Back to Team Management
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Invite Employee</h1>
          <p className="text-gray-600 mt-1">
            Send an invitation to join your team. They'll receive a secure link to create their account.
          </p>
        </div>

        {/* Form Card */}
        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email - ✅ UPDATED with validation feedback */}
            <div>
              <label className="label">
                <Mail size={16} className="inline mr-1" />
                Email Address *
              </label>
              <div className="relative">
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`input ${emailError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="employee@example.com"
                  maxLength="255"
                />
                {checkingEmail && (
                  <div className="absolute right-3 top-3">
                    <Loader className="animate-spin text-gray-400" size={20} />
                  </div>
                )}
              </div>
              {emailError ? (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {emailError}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  The employee will use this email to log in
                </p>
              )}
            </div>

            {/* First Name */}
            <div>
              <label className="label">
                <User size={16} className="inline mr-1" />
                First Name
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="input"
                placeholder="John"
                maxLength="100"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="label">
                <User size={16} className="inline mr-1" />
                Last Name
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="input"
                placeholder="Doe"
                maxLength="100"
              />
            </div>

            {/* Personal Message */}
            <div>
              <label className="label">
                <MessageSquare size={16} className="inline mr-1" />
                Personal Message (Optional)
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="input"
                rows="4"
                placeholder="Welcome to the team! Looking forward to working with you."
                maxLength="500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Add a personal welcome message (max 500 characters)
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">i</span>
                </div>
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-1">What happens next?</p>
                  <ul className="space-y-1 text-blue-800">
                    <li>• You'll receive a shareable invitation link</li>
                    <li>• Share the link via text, email, or any messaging app</li>
                    <li>• The invitation expires in 7 days</li>
                    <li>• Employee creates their own password during registration</li>
                    <li>• They'll have access to Customers, Jobs, and Leads</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Submit Button - ✅ Disabled if email error */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading || checkingEmail || !!emailError}
                className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Invitation...' : 'Create Invitation'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/team')}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteEmployee;